import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth, API } from "@/App";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { 
  Star, 
  Gift, 
  Award,
  ChevronRight,
  Copy,
  Check,
  Flame,
  Crown
} from "lucide-react";
import { toast } from "sonner";

// Smiley Component
const Smiley = ({ className = "", color = "#E85D3B" }) => (
  <svg viewBox="0 0 24 24" className={className} fill={color}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="8" cy="10" r="1.5" fill="#FDF6E9" />
    <circle cx="16" cy="10" r="1.5" fill="#FDF6E9" />
    <path d="M8 14c0 2.5 2 4 4 4s4-1.5 4-4" stroke="#FDF6E9" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

const rewardTiers = [
  { name: "Starter", minPoints: 0, icon: Star, color: "#7A6B5A" },
  { name: "Bronze", minPoints: 100, icon: Award, color: "#CD7F32" },
  { name: "Silber", minPoints: 300, icon: Award, color: "#C0C0C0" },
  { name: "Gold", minPoints: 500, icon: Crown, color: "#FFD700" },
  { name: "VIP", minPoints: 1000, icon: Crown, color: "#E85D3B" },
];

export default function MemberPage() {
  const { user, token, setShowAuthModal } = useAuth();
  const [copied, setCopied] = useState(false);

  const copyMemberCode = () => {
    if (user?.member_code) {
      navigator.clipboard.writeText(user.member_code);
      setCopied(true);
      toast.success("Code kopiert!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getCurrentTier = () => {
    if (!user) return rewardTiers[0];
    const points = user.points || 0;
    for (let i = rewardTiers.length - 1; i >= 0; i--) {
      if (points >= rewardTiers[i].minPoints) {
        return { ...rewardTiers[i], index: i };
      }
    }
    return { ...rewardTiers[0], index: 0 };
  };

  const getNextTier = () => {
    const current = getCurrentTier();
    if (current.index < rewardTiers.length - 1) {
      return rewardTiers[current.index + 1];
    }
    return null;
  };

  const getProgressToNext = () => {
    if (!user) return 0;
    const current = getCurrentTier();
    const next = getNextTier();
    if (!next) return 100;
    
    const pointsInTier = user.points - current.minPoints;
    const tierRange = next.minPoints - current.minPoints;
    return Math.min(100, (pointsInTier / tierRange) * 100);
  };

  if (!user) {
    return (
      <div 
        className="min-h-screen pb-safe flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: '#8ECFC0' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-sm"
        >
          <motion.div
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <Smiley className="w-24 h-24 mx-auto mb-6" />
          </motion.div>
          
          <h1 className="text-3xl font-extrabold mb-2" style={{ color: '#3D2314' }}>
            Member-Bereich
          </h1>
          <p className="mb-6" style={{ color: '#5C3D2E' }}>
            Sammle Punkte bei jedem Einkauf und löse sie gegen tolle Prämien ein.
          </p>
          
          <button 
            onClick={() => setShowAuthModal(true)} 
            className="btn-primary"
            style={{ backgroundColor: '#E85D3B' }}
          >
            Jetzt Mitglied werden
          </button>
          
          <p className="text-sm mt-4" style={{ color: '#5C3D2E' }}>
            1€ = 1 Punkt • Gratis Willkommens-Coupon ✨
          </p>
        </motion.div>
      </div>
    );
  }

  const currentTier = getCurrentTier();
  const nextTier = getNextTier();
  const progress = getProgressToNext();
  const TierIcon = currentTier.icon;

  return (
    <div className="min-h-screen pb-safe" style={{ backgroundColor: '#8ECFC0' }}>
      {/* Header */}
      <header className="px-6 pt-8 pb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Smiley className="w-10 h-10" />
            <div>
              <p className="text-sm" style={{ color: '#5C3D2E' }}>Hallo,</p>
              <h1 className="text-xl font-bold" style={{ color: '#3D2314' }}>{user.name}</h1>
            </div>
          </div>
          <div 
            className="flex items-center gap-2 px-4 py-2 rounded-full"
            style={{ backgroundColor: currentTier.color }}
          >
            <TierIcon className="w-4 h-4 text-white" />
            <span className="text-sm font-bold text-white">{currentTier.name}</span>
          </div>
        </div>
      </header>

      {/* Points Card */}
      <section className="px-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl p-6 text-center"
          style={{ backgroundColor: '#2D5A3D' }}
        >
          <p className="text-sm text-white/70 mb-2">Deine Punkte</p>
          <p className="text-6xl font-extrabold text-white mb-4">{user.points || 0}</p>
          
          {/* Progress to next tier */}
          {nextTier && (
            <div>
              <div className="flex justify-between text-xs text-white/70 mb-2">
                <span>{currentTier.name}</span>
                <span>{nextTier.name}</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                <motion.div 
                  className="h-full rounded-full"
                  style={{ backgroundColor: '#E85D3B' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-xs text-white/60 mt-2">
                Noch {nextTier.minPoints - user.points} Punkte bis {nextTier.name}
              </p>
            </div>
          )}
        </motion.div>
      </section>

      {/* QR Code Card */}
      <section className="px-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-3xl p-6 text-center"
          style={{ backgroundColor: '#FDF6E9' }}
        >
          <p className="text-sm mb-4" style={{ color: '#7A6B5A' }}>
            Zeige diesen Code beim Bezahlen
          </p>
          
          <div className="qr-container mx-auto mb-4">
            <QRCodeSVG
              value={user.member_code || "MEMBER"}
              size={150}
              bgColor="#FFFFFF"
              fgColor="#3D2314"
              level="H"
            />
          </div>
          
          <button
            onClick={copyMemberCode}
            className="flex items-center gap-2 mx-auto px-4 py-2 rounded-full"
            style={{ backgroundColor: '#E8DFD0' }}
          >
            <span className="font-mono font-bold" style={{ color: '#3D2314' }}>
              {user.member_code}
            </span>
            {copied ? (
              <Check className="w-4 h-4" style={{ color: '#2D5A3D' }} />
            ) : (
              <Copy className="w-4 h-4" style={{ color: '#7A6B5A' }} />
            )}
          </button>
        </motion.div>
      </section>

      {/* Quick Stats */}
      <section className="px-6 mb-6">
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Streak", value: "3 Tage", icon: Flame, color: "#E85D3B" },
            { label: "Coupons", value: "5", icon: Gift, color: "#2D5A3D" },
            { label: "Level", value: currentTier.name, icon: Award, color: currentTier.color },
          ].map((stat, i) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + i * 0.1 }}
                className="rounded-2xl p-4 text-center"
                style={{ backgroundColor: '#FDF6E9' }}
              >
                <Icon className="w-6 h-6 mx-auto mb-2" style={{ color: stat.color }} />
                <p className="font-bold" style={{ color: '#3D2314' }}>{stat.value}</p>
                <p className="text-xs" style={{ color: '#7A6B5A' }}>{stat.label}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Rewards to Unlock */}
      <section className="px-6 mb-6">
        <h2 className="font-bold mb-4" style={{ color: '#3D2314' }}>Prämien freischalten</h2>
        <div className="space-y-3">
          {[
            { title: "Gratis Kaffee", points: 50, emoji: "☕" },
            { title: "10% Rabatt", points: 100, emoji: "🏷️" },
            { title: "Cheesecake Tasting", points: 200, emoji: "🌿" },
            { title: "VIP Event", points: 500, emoji: "🎉" },
          ].map((reward, i) => {
            const isUnlocked = (user.points || 0) >= reward.points;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.05 }}
                className="flex items-center gap-4 p-4 rounded-2xl"
                style={{ 
                  backgroundColor: '#FDF6E9',
                  opacity: isUnlocked ? 1 : 0.6,
                  border: isUnlocked ? '2px solid #2D5A3D' : 'none'
                }}
              >
                <div className="text-3xl">{reward.emoji}</div>
                <div className="flex-1">
                  <h3 className="font-bold" style={{ color: '#3D2314' }}>{reward.title}</h3>
                  <p className="text-sm" style={{ color: '#7A6B5A' }}>{reward.points} Punkte</p>
                </div>
                <button
                  disabled={!isUnlocked}
                  className="px-4 py-2 rounded-full font-semibold text-sm"
                  style={{ 
                    backgroundColor: isUnlocked ? '#E85D3B' : '#E8DFD0',
                    color: isUnlocked ? '#FDF6E9' : '#7A6B5A'
                  }}
                >
                  {isUnlocked ? 'Einlösen' : '🔒'}
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
